import React, { forwardRef } from "react";
import clearLogoSrc from "../../assets/images/clear-logo.svg";
import { createBookletPageOrder } from "./BookletPageOrdering";
import { triadRoleOf } from "./projects.utils";
import "./Projects.css";

const ProjectFairPrint = forwardRef(function ProjectFairPrint(
  { fairDetails, projects, flipMode = 'short' }, ref) {
    const getProjectLogo = (p) => {
      if (p.logo && p.logo === 'GENERIC') return clearLogoSrc;
      if (p.logo && p.logo.indexOf('api/files') < 0)
        return `/api/files/images/project-fair/${p.year}/logos/${p.logo}`;
      if (p.logo && !p.logo.startsWith('/')) return `/${p.logo}`;
      return p.logo || clearLogoSrc;
    };

    /*
     * Roster roles are authored free-text and arrive under several spellings, so they are
     * resolved through the shared `triadRoleOf` rather than compared literally - the current
     * data set contains both "committee member" and "committee", and an equality check
     * against either one silently drops the other.
     */
    const getByTriadRole = (team, role) =>
      !team?.team ? [] : team.team.filter((t) => triadRoleOf(t?.role) === role);

    const getThanks = (team) =>
      !team?.team
        ? []
        : team.team.filter((t) => (t?.role ?? "").trim().toLowerCase() === "thanks");

    const createProjectPage = (project, projectNumber) => {
      if (!project) return <div className="blank-page"></div>;

      const mentees = getByTriadRole(project.team, "Mentee");
      const mentors = getByTriadRole(project.team, "Mentor");
      const committeeMembers = getByTriadRole(project.team, "Committee Member");
      const teamThanks = getThanks(project.team); // Thanks from team members
      const sponsors = project.exec_sponsor ?? [];
      
      // Handle thanks as a string from the projects table (already formatted)
      const projectThanksText = project.thanks && typeof project.thanks === 'string' && project.thanks.trim() 
        ? project.thanks.trim() 
        : '';
      
      const tagNames =
        project.tags?.map((tag) => tag?.tag?.name).filter(Boolean) ?? [];
      const location = project.Chapter?.location;

      return (
        <article className="project-half-page">
          <header className="project-header-booklet">
            <div className="project-header-main">
              <span className="project-number-booklet">
                <span className="project-number-label">Project</span>
                <span className="project-number-value">#{projectNumber}</span>
              </span>
              <h3 className="project-title-booklet">{project.title}</h3>
              <div className="project-meta-booklet">
                {location && (
                  <span className="project-meta-pill">{location}</span>
                )}
                {project.year && (
                  <span className="project-meta-pill project-meta-year">
                    {project.year}
                  </span>
                )}
              </div>
            </div>
            <div className="project-logo-booklet">
              <img
                src={getProjectLogo(project)}
                alt={`${project.title} Logo`}
                className="booklet-logo"
              />
            </div>
          </header>

          <div className="project-content-booklet">
            {tagNames.length > 0 && (
              <ul className="project-tags-booklet">
                {tagNames.map((name, idx) => (
                  <li
                    key={`${project.id ?? project.title ?? 'project'}-tag-${idx}`}
                    className="project-tag-chip"
                  >
                    {name}
                  </li>
                ))}
              </ul>
            )}

            {project.description && (
              <section className="project-description-booklet">
                <h4 className="project-section-heading">Overview</h4>
                <p>{project.description}</p>
              </section>
            )}

            {(mentees.length > 0 || mentors.length > 0 || committeeMembers.length > 0) && (
              <section className="project-team-booklet">
                <h4 className="project-section-heading">Team</h4>
                <dl className="project-team-grid">
                  {mentees.length > 0 && (
                    <>
                      <dt>Mentees</dt>
                      <dd>{mentees.map((m) => m.name).join(', ')}</dd>
                    </>
                  )}
                  {mentors.length > 0 && (
                    <>
                      <dt>Mentors</dt>
                      <dd>{mentors.map((m) => m.name).join(', ')}</dd>
                    </>
                  )}
                  {committeeMembers.length > 0 && (
                    <>
                      <dt>Committee</dt>
                      <dd>{committeeMembers.map((m) => m.name).join(', ')}</dd>
                    </>
                  )}
                </dl>
              </section>
            )}

            {sponsors.length > 0 && (
              <section className="project-sponsors-booklet">
                <h4 className="project-section-heading">Sponsors</h4>
                <p>{sponsors.map((s) => [s.name, s.title].filter(Boolean).join(' — ')).join(', ')}</p>
              </section>
            )}

            {(teamThanks.length > 0 || projectThanksText) && (
              <section className="project-thanks-booklet">
                <h4 className="project-section-heading">Special Thanks</h4>
                <p>
                  {teamThanks.length > 0 && teamThanks.map((t) => t.name).join(', ')}
                  {teamThanks.length > 0 && projectThanksText && ', '}
                  {projectThanksText}
                </p>
              </section>
            )}
          </div>
        </article>
      );
    };

    const createCoverPage = () => (
      <div className="title-page">
        <div className="title-header">
          <div className="title-bg-image"></div>
          <h1 className="booklet-title">{fairDetails.title}</h1>
          <p className="booklet-date">{fairDetails.friendly_date}</p>
        </div>
        <div className="title-footer">
          {fairDetails.survey_text && (
            <p><strong>Survey:</strong> {fairDetails.survey_text}</p>
          )}
        </div>
      </div>
    );

    const createThankYouPage = () => (
      <div className="back-cover">
        <h3>Thank You</h3>
        {fairDetails.closing_msg
          ? fairDetails.closing_msg.split("\n").map((line, i) => <p key={i}>{line}</p>)
          : <p>Thank you for attending the Project Fair!</p>}
      </div>
    );

    const createWelcomePage = () => (
      <div className="welcome-page">
        <h2>Welcome</h2>
        {fairDetails.welcome_msg && (
          <div className="welcome-content">
            {fairDetails.welcome_msg.split("\n").map((line, i) => <p key={i}>{line}</p>)}
          </div>
        )}
        {fairDetails.directions && (
          <div className="directions-content">
            <h3>How to Participate</h3>
            {fairDetails.directions.split("\n").map((line, i) => <p key={i}>{line}</p>)}
          </div>
        )}
      </div>
    );

    const createTableOfContentsPage = (list) => (
      <div className="toc-page">
        <h2>Table of Contents</h2>
        <div className="toc-list">
          {list.map((p, i) => (
            <div key={p.id ?? `toc-${i}`} className="toc-item">
              <span className="toc-number">{i + 1}</span>
              <span className="toc-title">{p.title}</span>
              <span className="toc-location">{p.Chapter?.location || ''}</span>
            </div>
          ))}
        </div>
      </div>
    );

    const renderPageContent = (content) => {
      if (!content) return <div className="blank-page"></div>;
      switch (content.type) {
        case 'cover':     return createCoverPage();
        case 'thank-you': return createThankYouPage();
        case 'welcome':   return createWelcomePage();
        case 'toc':       return createTableOfContentsPage(content.data);
        case 'project':
          return content.data
            ? createProjectPage(content.data, content.projectNumber)
            : <div className="blank-page"></div>; // blank if no project (padding)
        default:          return <div className="blank-page"></div>;
      }
    };

    // Generate pages with chosen duplex flip mode
        const bookletPages = createBookletPageOrder(projects, fairDetails, {
          flipMode,           // 'short' for your print dialog
          layoutMode: 'simple'// <- THIS matches: Title | (flip) Welcome | TOC | (flip) 1|2 | (flip) 3|4 | ...
        });
        
    return (
      <div ref={ref} className="booklet-flyer-print print-only">
        {bookletPages.map(page => (
          <div key={`page-${page.pageIndex}`} className="booklet-page">
            <div className="booklet-half booklet-left">
              <div className="booklet-sheet">
                {renderPageContent(page.leftContent)}
              </div>
            </div>
            <div className="booklet-half booklet-right">
              <div className="booklet-sheet">
                {renderPageContent(page.rightContent)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
);

export default ProjectFairPrint;
